import React, { useState } from "react";
import Header from "./Header";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { signOut } from "@/services/authService";
import { useToast } from "@/hooks/use-toast";
import { NEW_APPLICATION, useApplication } from "@/contexts/ApplicationContext";

interface AppLayoutProps {
  children: React.ReactNode;
  showJobPosting?: boolean;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  showJobPosting = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { setApplication } = useApplication();

  const handleBackToJobPostings = async () => {
    setIsLoading(true);
    try {
      // Clear application data from local storage
      localStorage.removeItem("currentApplicationId");
      localStorage.removeItem("currentJobId");

      // Sign out from Supabase auth
      const { success, error } = await signOut();
      setApplication(NEW_APPLICATION);

      if (success) {
        window.location.href = "/";
      } else {
        console.error("Error signing out:", error);
        toast({
          title: "Error",
          description: "There was an issue signing out. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Unexpected error during sign out:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 pt-4 pb-4 max-w-7xl mx-auto">
        <Button
          variant="ghost"
          className="text-gray-600 hover:text-black hover:bg-gray-100 flex items-center gap-1 mb-2"
          onClick={handleBackToJobPostings}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Please wait...
            </>
          ) : (
            <>
              <ArrowLeft className="h-4 w-4" />
              Back to Job Postings
            </>
          )}
        </Button>

        <Header showJobPosting={showJobPosting} />
      </div>
      <main className="max-w-7xl mx-auto px-4">{children}</main>
    </div>
  );
};

export default AppLayout;
